interface ApiErrorEnvelope {
  response?: {
    status?: unknown;
    data?: {
      error?: {
        message?: unknown;
        code?: unknown;
        details?: unknown;
      };
      message?: unknown;
    };
  };
  message?: unknown;
}

interface NormalizedApiError<TDetails extends object = Record<string, unknown>> {
  message: string;
  code?: string | number;
  details?: TDetails;
}

function normalizeMessage(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim()) return value;
  if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string' && Boolean(item.trim())).join('\n') || undefined;
  return undefined;
}

function normalizeCode(value: unknown): string | number | undefined {
  if (typeof value === 'string' || typeof value === 'number') return value;
  return undefined;
}

function normalizeDetails<TDetails extends object>(value: unknown): TDetails | undefined {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) return value as TDetails;
  return undefined;
}

export function getApiError<TDetails extends object = Record<string, unknown>>(error: unknown): NormalizedApiError<TDetails> | null {
  if (error === null || error === undefined) return null;

  const apiError = error as ApiErrorEnvelope;
  const envelopeError = apiError.response?.data?.error;
  const message =
    normalizeMessage(envelopeError?.message) ??
    normalizeMessage(apiError.response?.data?.message) ??
    normalizeMessage(apiError.message);

  if (!message) return null;

  return {
    message,
    code: normalizeCode(envelopeError?.code),
    details: normalizeDetails<TDetails>(envelopeError?.details),
  };
}

export function getApiErrorMessage(error: unknown, fallback: string): string {
  return getApiError(error)?.message ?? fallback;
}

/** HTTP status code của response lỗi, nếu có (vd 403/404/400). */
export function getApiErrorStatus(error: unknown): number | undefined {
  if (error === null || error === undefined) return undefined;
  const status = (error as ApiErrorEnvelope).response?.status;
  return typeof status === 'number' ? status : undefined;
}

/**
 * TCR-1473 — true khi lỗi là 403 Forbidden (record ngoài phạm vi
 * `assigned_sale_ids` của user, hoặc thiếu permission action-level).
 * Dùng ở trang chi tiết KH/đơn hàng để phân biệt RÕ "không có quyền xem"
 * với "không tìm thấy" (404) — 2 case UI khác hẳn nhau, không được gộp
 * chung 1 message "Không tìm thấy" gây hiểu lầm cho người dùng.
 */
export function isForbiddenError(error: unknown): boolean {
  return getApiErrorStatus(error) === 403;
}

/**
 * TCR-2523 — true khi BE trả 409 Conflict.
 *
 * Ở luồng "kéo tồn kho AMIS" 409 là TRẠNG THÁI BÌNH THƯỜNG (đang có lượt kéo
 * khác chạy cho cùng cửa hàng), không phải lỗi hệ thống — nên phải hiện
 * thông báo riêng thay vì lỗi trần.
 */
export function isConflictError(error: unknown): boolean {
  return getApiErrorStatus(error) === 409;
}

/**
 * Chỉ lấy message BE trả trong envelope — KHÔNG rơi về `error.message` của axios.
 *
 * Khác `getApiError` ở đúng chỗ đó: dùng khi thà hiện câu tiếng Việt tự viết còn
 * hơn để lọt "Request failed with status code 400" ra trước mặt người dùng.
 */
export function getApiEnvelopeMessage(error: unknown): string | undefined {
  const apiError = error as ApiErrorEnvelope;
  return (
    normalizeMessage(apiError.response?.data?.error?.message) ??
    normalizeMessage(apiError.response?.data?.message)
  );
}
